import { create } from 'zustand';
import { authApi, type AuthUser } from './authApi';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

type Step = 'email' | 'code' | 'authenticated';

interface AuthStore {
  // State
  step:      Step;
  email:     string;
  token:     string | null;
  user:      AuthUser | null;
  loading:   boolean;
  error:     string | null;

  // Actions
  requestCode: (email: string, turnstileToken: string | null) => Promise<void>;
  verifyCode:  (code: string)  => Promise<void>;
  logout:      ()              => Promise<void>;
  /** Called once on app startup to restore a persisted session. */
  hydrate:     ()              => Promise<void>;
  clearError:  ()              => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Store
// ─────────────────────────────────────────────────────────────────────────────

const TOKEN_KEY = 'darkage_token';

export const useAuthStore = create<AuthStore>((set, get) => ({
  step:    'email',
  email:   '',
  token:   null,
  user:    null,
  loading: !!localStorage.getItem(TOKEN_KEY),
  error:   null,

  clearError: () => set({ error: null }),

  // ── Step 1: send magic code ───────────────────────────────────────────────
  requestCode: async (email, turnstileToken) => {
    set({ loading: true, error: null, email });
    try {
      const res = await authApi.requestCode(email, turnstileToken);
      // In dev the code is returned in the response — log it for convenience
      if (res._dev_code) {
        console.info(`[DEV] Magic code for ${email}: ${res._dev_code}`);
      }
      set({ step: 'code', loading: false });
    } catch (e) {
      set({ loading: false, error: (e as Error).message });
    }
  },

  // ── Step 2: validate code ─────────────────────────────────────────────────
  verifyCode: async (code) => {
    const { email } = get();
    set({ loading: true, error: null });
    try {
      const res = await authApi.verifyCode(email, code);
      localStorage.setItem(TOKEN_KEY, res.token);
      set({ step: 'authenticated', token: res.token, user: res.user, loading: false });
    } catch (e) {
      set({ loading: false, error: (e as Error).message });
    }
  },

  // ── Logout ────────────────────────────────────────────────────────────────
  logout: async () => {
    const { token } = get();
    if (token) {
      try { await authApi.logout(token); } catch { /* best-effort */ }
    }
    localStorage.removeItem(TOKEN_KEY);
    set({ step: 'email', token: null, user: null, email: '', error: null });
  },

  // ── Hydrate session from localStorage ────────────────────────────────────
  hydrate: async () => {
    const saved = localStorage.getItem(TOKEN_KEY);
    if (!saved) return;
    set({ loading: true });
    try {
      const res = await authApi.me(saved);
      set({ step: 'authenticated', token: saved, user: res.user, loading: false });
    } catch {
      // Token expired or invalid — clear it silently
      localStorage.removeItem(TOKEN_KEY);
      set({ loading: false });
    }
  },
}));
